import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQueryWithReauth';

export interface Project {
  id: string;
  title: string;
  quote_id?: string | null;
  progress?: number;
  milestones?: Milestone[];
}

export interface Milestone {
  id: string;
  name: string;
  progress: number;
  status: string;
}

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Project'],
  endpoints: (builder) => ({
    getProjects: builder.query<Project[], void>({
      query: () => ({ url: '/projects' }),
      providesTags: ['Project'],
    }),
    getDashboard: builder.query<Project[], void>({
      query: () => ({ url: '/dashboard/projects' }),
      providesTags: ['Project'],
    }),
    getProjectByQuote: builder.query<Project, string>({
      query: (quoteId) => ({ url: `/projects/quote/${quoteId}` }),
      providesTags: ['Project'],
    }),
    createProject: builder.mutation<Project, Partial<Project> & { customer_id: string }>({
      query: (body) => ({
        url: '/projects',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Project'],
    }),
    deleteProject: builder.mutation<void, string>({
      query: (id) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Project'],
    }),
    getMilestones: builder.query<Milestone[], string>({
      query: (projectId) => ({ url: `/projects/${projectId}/milestones` }),
      providesTags: ['Project'],
    }),
    addMilestone: builder.mutation<Milestone, { projectId: string; body: Partial<Milestone> }>({
      query: ({ projectId, body }) => ({
        url: `/projects/${projectId}/milestones`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Project'],
    }),
    updateMilestone: builder.mutation<Milestone, { projectId: string; milestoneId: string; body: Partial<Milestone> }>({
      query: ({ projectId, milestoneId, body }) => ({
        url: `/projects/${projectId}/milestones/${milestoneId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Project'],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useGetDashboardQuery,
  useGetProjectByQuoteQuery,
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useGetMilestonesQuery,
  useAddMilestoneMutation,
  useUpdateMilestoneMutation,
} = projectsApi;
